import React, { useEffect, useState } from "react";
import PaginationComponentOne from "../../components/Pagination/Pagination-O";
import Header from "../../components/Header";
import { useDispatch, useSelector } from "react-redux";
import { getOutOfStockProducts } from "../../redux/Inventory/inventoryActions";
import EmptyState from "../../components/EmptyState";
import { Link } from "react-router-dom";
import moment from "moment";

const OutOfStockPage = () => {
  const dispatch = useDispatch();
  const { outOfStockProductsData, outOfStockLoading, outOfStockTotalCount } = useSelector((state) => state.inventory);
  
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [completelyOutOfStock, setCompletelyOutOfStock] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  
  // Store all locations and categories to maintain full list when filtering
  const [allLocations, setAllLocations] = useState([]);
  const [allCategories, setAllCategories] = useState([]);

  // Update all locations and categories when we get unfiltered data
  useEffect(() => {
    if (!selectedLocation && !selectedCategory && !search && !completelyOutOfStock && outOfStockProductsData?.length > 0) {
      // Extract unique locations
      const locationMap = new Map();
      outOfStockProductsData.forEach(product => {
        product.out_of_stock_locations?.forEach(location => {
          if (!locationMap.has(location.location_id)) {
            locationMap.set(location.location_id, {
              id: location.location_id,
              name: location.location_name
            });
          }
        });
      });
      const locations = Array.from(locationMap.values()).sort((a, b) => a.name.localeCompare(b.name));
      if (locations.length > allLocations.length) {
        setAllLocations(locations);
      }
      
      // Extract unique categories
      const categories = new Set();
      outOfStockProductsData.forEach(product => {
        if (product.category) {
          categories.add(product.category);
        }
      });
      const categoriesArray = Array.from(categories).sort();
      if (categoriesArray.length > allCategories.length) {
        setAllCategories(categoriesArray);
      }
    }
  }, [outOfStockProductsData, selectedLocation, selectedCategory, search, completelyOutOfStock, allCategories, allLocations]);

  useEffect(() => {
    const filters = {
      page,
      page_size: pageSize,
      search: search || undefined,
      location_id: selectedLocation || undefined,
      category: selectedCategory || undefined,
      completely_out_of_stock: completelyOutOfStock ? true : undefined,
    };
    dispatch(getOutOfStockProducts(filters));
  }, [dispatch, page, pageSize, search, selectedLocation, selectedCategory, completelyOutOfStock]);

  const handlePageChange = (paginationData) => {
    // Carbon's Pagination component passes an object with page and pageSize
    if (typeof paginationData === 'object') {
      if (paginationData.page !== undefined) {
        setPage(paginationData.page);
      }
      if (paginationData.pageSize !== undefined && paginationData.pageSize !== pageSize) {
        setPageSize(paginationData.pageSize);
        setPage(1); // Reset to first page when page size changes
      }
    } else {
      setPage(paginationData);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1); // Reset to first page on search
  };

  const formatVariantAttributes = (attributes) => {
    if (!attributes) return "";
    return Object.entries(attributes)
      .map(([key, value]) => `${key}: ${value}`)
      .join(", ");
  };

  return (
    <>
      <Header text="Out of Stock" />

      <div className="mt-5 bg-white rounded-[8px] border border-[#E1E6E1] py-2 px-4 col-span-3">
        {/* Table Filters */}
        <div className="py-4 flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px] max-w-[400px]">
              <input
              type="text"
              placeholder="Search product name or SKU..."
              value={search}
              onChange={handleSearch}
               className="appearance-none w-full placeholder:text-[#96A397] 
                placeholder:text-sm text-[#344335] text-base focus:border-[#2B8C34] 
                focus:outline-none focus:shadow-input rounded-lg border border-[#96A397] bg-white px-4 py-2"
              />
          </div>
          
          <div className="min-w-[150px]">
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2 border border-[#E1E6E1] rounded-md focus:outline-none focus:ring-2 focus:ring-[#2B8C34] text-sm"
            >
              <option value="">All Categories</option>
              {allCategories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div className="min-w-[150px]">
            <select
              value={selectedLocation}
              onChange={(e) => {
                setSelectedLocation(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2 border border-[#E1E6E1] rounded-md focus:outline-none focus:ring-2 focus:ring-[#2B8C34] text-sm"
            >
              <option value="">All Locations</option>
              {allLocations.map(location => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={completelyOutOfStock}
                onChange={(e) => {
                  setCompletelyOutOfStock(e.target.checked);
                  setPage(1);
                }}
                className="w-4 h-4 text-[#2B8C34] rounded focus:ring-[#2B8C34]"
              />
              <span className="text-sm text-[#5F6D60]">Completely out of stock</span>
            </label>
          </div>
        </div>

        <div className="overflow-x-auto relative py-4">
          {outOfStockLoading ? (
            <div className="animate-pulse w-full mb-4">
              <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg w-full"></div>
            </div>
          ) : !outOfStockProductsData || outOfStockProductsData.length === 0 ? (
            <div className="">
              <EmptyState text="No out of stock products found!" />
            </div>
          ) : (
            <>
              <table className="w-full text-left rounded-lg overflow-hidden border border-[#E1E6E1]">
                <thead className="bg-[#F6F7F6] border-[#E1E6E1]">
                  <tr className="text-sm">
                    <th className="px-4 py-4 font-semibold text-[#5F6D60] whitespace-nowrap">
                      Product Information
                    </th>
                    <th className="px-4 py-4 font-semibold text-[#5F6D60]">
                      Category
                    </th>
                    <th className="px-4 py-4 font-semibold text-[#5F6D60]">
                      Out of Stock Locations
                    </th>
                    <th className="px-4 py-4 font-semibold text-[#5F6D60]">
                      Last Restocked
                    </th>
                    <th className="px-4 py-4 font-semibold text-[#5F6D60]">
                      Supplier
                    </th>
                    <th className="px-4 py-4 font-semibold text-[#5F6D60]">
                      Status
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white border-[#E1E6E1]">
                  {outOfStockProductsData.map((product) => (
                    <tr
                      key={product.inventory_id}
                      className="text-sm cursor-pointer hover:bg-[#EDF7EE] border-b border-[#E1E6E1]"
                    >
                      <td className="px-4 py-4">
                        <Link
                          to={`/product-inventory/${product.product_id}${product.is_variant ? `?variant=${product.variant_id}` : ""}`}
                          className="hover:text-[#2B8C34]"
                        >
                          <div>
                            <p className="font-semibold text-[#5F6D60]">
                              {product.product_name}
                            </p>
                            <p className="text-xs text-gray-500">
                              SKU: {product.is_variant ? product.variant_sku : product.product_sku}
                            </p>
                            {product.is_variant && (
                              <p className="text-xs text-gray-500 mt-1">
                                {formatVariantAttributes(product.variant_attributes)}
                              </p>
                            )}
                          </div>
                        </Link>
                      </td>

                      <td className="px-4 py-4 text-[#5F6D60]">
                        <div>
                          <p>{product.category || "-"}</p>
                          {product.subcategory && (
                            <p className="text-xs text-gray-500">{product.subcategory}</p>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div>
                          <p className="font-semibold text-[#B92020]">
                            {product.total_locations_out_of_stock} of {product.total_locations_with_inventory} locations
                          </p>
                          <div className="mt-1">
                            {product.out_of_stock_locations.slice(0, 2).map((location, idx) => (
                              <p key={idx} className="text-xs text-gray-500">
                                {location.location_name} 
                                {location.last_stock_out_date && 
                                  ` (Out since ${moment(location.last_stock_out_date).format("MMM DD")})`
                                }
                              </p>
                            ))}
                            {product.out_of_stock_locations.length > 2 && (
                              <p className="text-xs text-gray-500">
                                +{product.out_of_stock_locations.length - 2} more
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4 text-[#5F6D60]">
                        {product.last_restocked_date 
                          ? moment(product.last_restocked_date).format("MMM DD, YYYY")
                          : "Never"
                        }
                      </td>

                      <td className="px-4 py-4 text-[#5F6D60]">
                        {product.supplier_name || "-"}
                      </td>

                      <td className="px-4 py-4">
                        <div className="relative">
                          <div className={product.is_completely_out_of_stock 
                            ? "out-of-stock-badge bg-[#FEE2E2] text-[#B92020]" 
                            : "out-of-stock-badge"
                          }>
                            <p>
                              {product.is_completely_out_of_stock 
                                ? "Completely Out" 
                                : "Out of Stock"
                              }
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {/* Pagination */}
          {outOfStockProductsData && outOfStockProductsData.length > 0 && (
            <div className="border-t border-[#E1E6E1]">
              <PaginationComponentOne
                totalItems={outOfStockTotalCount || 0}
                page={page}
                pageSize={pageSize}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OutOfStockPage;